import { type Location } from "react-router-dom";

import {
  getQueryParams,
  type QueryForm,
  type QueryParamValue,
  transformQueryParams,
} from "@north-beam/nb-common";

import {
  type Page,
  type PageVersion,
  type PageId,
  type PageLabel,
  Pages,
  findPageForPath,
} from "../../../constants";
import { type AbstractUser } from "../../../user";

export type LoggerPathLocation = {
  readonly path: string;
  readonly pathParams?: Record<string, string | number>;
  readonly queryParams?: QueryForm;
};

export type LoggerPageLocation<I extends PageId = PageId> = {
  readonly page: Page<I> | I;
  readonly pathParams?: Record<string, string | number>;
  readonly queryParams?: QueryForm;
};

/**
 * Represents the specific location in the application where the {@link Logger} is being used,
 * which is a required parameter in the context provided to the {@link Logger}'s
 * {@link Logger.event} method.
 *
 * Since this {@link Logger} supports both web and mobile (at least currently, potentially more in
 * the future), the manner in which the 'location' is included in the context needs to be flexible,
 * since routing mechanisms are different between web and mobile.
 *
 * If the {@link Logger} is being used in a context for which the 'react-router-dom' package is not
 * available, the {@link LoggerLocation} can be provided explicitly as either the path to a
 * {@link Page} or the {@link Page} itself, along with any relevant path or query params.
 *
 * If the path is provided explicitly, or the {@link Location} is provided from the
 * 'react-router-dom' package, the {@link Page} that the path corresponds to will be automatically
 * determined and included in the data and message provided to Amplitude's 'logEvent' method.
 */
export type LoggerLocation<I extends PageId = PageId> =
  | Location
  | LoggerPathLocation
  | LoggerPageLocation<I>;

const locationIsPage = <I extends PageId>(
  loc: Location | LoggerPathLocation | LoggerPageLocation<I>,
): loc is LoggerPageLocation<I> => {
  return (loc as LoggerPageLocation<I>).page !== undefined;
};

const locationIsPath = <I extends PageId>(
  loc: Location | LoggerPathLocation | LoggerPageLocation<I>,
): loc is LoggerPathLocation => {
  return (loc as LoggerPathLocation).path !== undefined;
};

export type LoggerParsedPage<I extends PageId = PageId> = {
  readonly id: I;
  readonly label: PageLabel<I>;
  readonly version: PageVersion<I>;
};

export type LoggerSuccessfullyParsedLocation<I extends PageId = PageId> = {
  readonly path: string;
  readonly pathParams?: Record<string, string | number>;
  readonly queryParams?: Record<string, QueryParamValue>;
  readonly page: LoggerParsedPage<I>;
};

/* This constant is used as a placeholder for a parsed location value when the location cannot be
   determined from the provided Page or the Page does not have a 'pathMatch' value. */
export const INVALID_LOCATION = "Invalid Location" as const;

/**
 * Indicates that the transformation of the {@link LoggerLocation} object into a
 * {@link LoggerSuccessfullyParsedLocation} object was not possible, due to the pathname not having
 * an associated {@link Page} or the {@link Page} not having a 'pathMatch' value.
 */
export type LoggerFailedParsedLocation = {
  label: typeof INVALID_LOCATION;
  error: string;
};

export type LoggerParsedLocation<I extends PageId = PageId> =
  | LoggerFailedParsedLocation
  | LoggerSuccessfullyParsedLocation<I>;

export const isInvalidLocation = <I extends PageId>(
  loc: LoggerLocation<I> | LoggerParsedLocation<I>,
): loc is LoggerFailedParsedLocation =>
  (loc as LoggerFailedParsedLocation).label === INVALID_LOCATION;

/**
 * Parses the flexibly defined {@link LoggerLocation} into a consistent location form,
 * {@link LoggerParsedLocation}, that can be reliably used by the {@link Logger}'s transformation
 * methods to include information about the location in which the event occurred in the context and
 * message that is provided to Amplitude's 'logEvent' method.
 *
 * @param {LoggerLocation} loc
 *   The location that is provided in the context to the {@link Logger}'s {@link Logger.event}
 *   method, which represents the specific page in the application where the event was triggered.
 *
 * @param {U} user
 *   The user that is in the current context of the {@link Logger}.
 *
 * @returns {LoggerParsedLocation | null}
 *   If the {@link Page} could not be determined from the provided {@link LoggerLocation}, null is
 *   returned.  This will cause the {@link Logger}'s {@link Logger.event} method to exit early,
 *   rather than emitting to Amplitude.
 */
export const parseLocation = <U extends AbstractUser, I extends PageId>(
  loc: LoggerLocation<I>,
  user: U,
): LoggerParsedLocation<I> => {
  let path: [Page<I>, string];
  if (locationIsPath(loc)) {
    const pg = findPageForPath(loc.path, { strict: false });
    if (pg) {
      path = [pg as Page<I>, loc.path];
    } else {
      return {
        label: INVALID_LOCATION,
        error:
          `The path ${loc.path} is not associated with a 'pathMatch' value for any given Page ` +
          "in the Pages literals!",
      };
    }
  } else if (locationIsPage(loc)) {
    const pg =
      typeof loc.page === "string" ? Pages.getModel(loc.page) : loc.page;
    const pathMatch = pg.pathMatch;
    if (pathMatch === null) {
      return {
        label: INVALID_LOCATION,
        error:
          `The page ${pg.value} is not associated with a 'pathMatch'.  As such, it cannot be ` +
          "directly referenced as a logger location!",
      };
    }
    path = [pg as Page<I>, pathMatch];
  } else {
    const pg = findPageForPath(loc.pathname, { strict: false });
    if (pg) {
      path = [pg as Page<I>, loc.pathname];
    } else {
      return {
        label: INVALID_LOCATION,
        error:
          `The path ${loc.pathname} is not associated with a 'pathMatch' value for any given Page ` +
          "in the Pages literals!",
      };
    }
  }

  return {
    queryParams:
      locationIsPath(loc) || locationIsPage(loc)
        ? transformQueryParams(loc.queryParams || {}, {
            as: "object",
            enforceValidUrl: false,
          })
        : getQueryParams(loc.search, {
            as: "object",
            enforceValidUrl: false,
          }),
    pathParams:
      locationIsPath(loc) || locationIsPage(loc) ? loc.pathParams : undefined,
    page: {
      id: path[0].value as LoggerParsedPage<I>["id"],
      label: (typeof path[0].label === "function"
        ? path[0].label(user)
        : path[0].label) as LoggerParsedPage<I>["label"],
      version: (typeof path[0].version === "function"
        ? path[0].version(user)
        : path[0].version) as LoggerParsedPage<I>["version"],
    },
    path: path[1],
  };
};

export const locationIsParsed = <I extends PageId>(
  loc: LoggerLocation<I> | LoggerParsedLocation<I>,
): loc is LoggerParsedLocation<I> => {
  return (
    isInvalidLocation(loc) ||
    ((loc as LoggerSuccessfullyParsedLocation<I>).page !== undefined &&
      (loc as LoggerSuccessfullyParsedLocation<I>).page.id !== undefined)
  );
};
